import { faRepeat } from '@fortawesome/pro-solid-svg-icons';
import { format } from 'date-fns';
import { useAtomValue } from 'jotai';
import { useState } from 'react';

import { Icon, InputControl, Text, TimeRangeInput } from '@growtherapy/sprout-ui';

import { sessionInfoAtom } from '../../state';
import { AppointmentLocation, AppointmentType, RecurringAppointmentGrouping } from '../schedule-preview/types';
import { AppointmentLocationInput } from './AppointmentLocationInput';
import { DurationInput } from './DurationInput';
import { EditAppointmentFormValues } from './EditAppointmentDrawer';
import { RecurrenceInputWrapper as RecurrenceInput } from './RecurrenceInput';
import { SchedulingAlerts } from './SchedulingAlert';
import { BASE_DURATION_OPTIONS, DEFAULT_DURATION_IN_MINUTES, PRESCRIBER_DURATION_OPTION } from './constants';
import { GetSchedulingAlertsQueryData } from './useGetSchedulingAlerts';
import { getBrowserTimezone, getRecurringAppointmentText } from './utils';

interface ScheduleAppointmentFormProps {
  formValues: EditAppointmentFormValues;
  onUpdateValues: (values: Partial<EditAppointmentFormValues>) => void;
  appointmentType: AppointmentType;
  recurringAppointmentGrouping?: RecurringAppointmentGrouping | null;
  alertsData?: GetSchedulingAlertsQueryData;
  timeInputErrorMessage?: string;
  isRecurrenceEditable?: boolean;
  isPrescriber: boolean;
}

export function ScheduleAppointmentForm({
  formValues,
  onUpdateValues,
  recurringAppointmentGrouping,
  alertsData,
  timeInputErrorMessage,
  isRecurrenceEditable,
  isPrescriber,
}: ScheduleAppointmentFormProps) {
  const { startDate, endDate } = formValues;

  const durationOptions = isPrescriber ? [PRESCRIBER_DURATION_OPTION, ...BASE_DURATION_OPTIONS] : BASE_DURATION_OPTIONS;
  const [duration, setDuration] = useState(DEFAULT_DURATION_IN_MINUTES);
  const [appointmentLocation, setAppointmentLocation] = useState(AppointmentLocation.Virtual);

  // Updates endDate when duration changes
  const handleDurationChange = (duration: number): void => {
    setDuration(duration);
    const newEndDate = new Date(startDate);
    newEndDate.setMinutes(newEndDate.getMinutes() + duration);
    onUpdateValues({ endDate: newEndDate });
  };

  const handleAppointmentLocationChange = (location: AppointmentLocation): void => {
    setAppointmentLocation(location);
    onUpdateValues({ appointmentLocation: location });
  };

  return (
    <form>
      <InputControl
        className='mb-4'
        icon='clock'
        iconClassName='text-lilac-700'
        required
        aria-label='Appointment time'
        error={timeInputErrorMessage}
      >
        <div className='flex flex-wrap gap-4'>
          <TimeRangeInput
            containerClassName='
              contents                                
              [&>*:first-child]:basis-full            
              [&>*:first-child]:min-w-[10rem]     

              [&>*:last-child>div]:flex-1
              [&>*:last-child>div]:basis-full
              [&>*:last-child>div]:min-w-[10rem]
            '
            data-testid='schedule-event-time'
            minDate={new Date()}
            dateLabelClassName='text-neutral-800 text-medium pt-1 font-normal'
            onUpdateValues={onUpdateValues}
            values={{ startDate, endDate }}
            includeEndDate={false}
            preserveDuration
            disableManualEntry
            timeIntervals={5}
            dateLabel='Date'
            fullWidth={true}
            startLabel={`Start time (${getBrowserTimezone()})`}
            startLabelClassName='text-neutral-800 text-medium pt-1 font-normal'
          />

          <div className='flex-1 basis-0 min-w-[10rem]'>
            <DurationInput
              data-testid='schedule-event-duration'
              error={!!timeInputErrorMessage}
              onChange={handleDurationChange}
              value={duration}
              className='w-full text-medium'
              aria-label='Duration'
              durationOptions={durationOptions}
            />
          </div>
          <div className='text-center text-sm text-neutral-700 sm:text-left'>
            {`${format(startDate, 'EEE, MMM d')} at ${format(startDate, 'h:mm a')} - ${format(endDate, 'h:mm a')} (${getBrowserTimezone()})`}
          </div>
        </div>
      </InputControl>
      <div className='flex items-center gap-4 mb-4'>
        {isRecurrenceEditable ? (
          <RecurrenceInput values={formValues} onUpdateValues={onUpdateValues} />
        ) : (
          <>
            <Icon aria-hidden icon={faRepeat} className='text-lilac-700' />
            <Text variant='md' className='text-neutral-800 text-medium'>
              {getRecurringAppointmentText(recurringAppointmentGrouping)}
            </Text>
          </>
        )}
      </div>
      <InputControl icon='location-dot' iconClassName='text-lilac-700' required aria-label='Appointment location'>
        <AppointmentLocationInput onChange={handleAppointmentLocationChange} value={appointmentLocation} />
      </InputControl>
      {alertsData && <SchedulingAlerts alertsData={alertsData} />}
    </form>
  );
}

export function ScheduleAppointmentFormWrapper({
  formValues,
  onUpdateValues,
  appointmentType,
  recurringAppointmentGrouping,
  alertsData,
  timeInputErrorMessage,
  isRecurrenceEditable,
}: Omit<ScheduleAppointmentFormProps, 'isPrescriber'>) {
  const sessionInfo = useAtomValue(sessionInfoAtom);
  const isPrescriber = sessionInfo?.provider?.isPrescriber ?? false;

  return (
    <ScheduleAppointmentForm
      formValues={formValues}
      onUpdateValues={onUpdateValues}
      appointmentType={appointmentType}
      recurringAppointmentGrouping={recurringAppointmentGrouping}
      alertsData={alertsData}
      timeInputErrorMessage={timeInputErrorMessage}
      isRecurrenceEditable={isRecurrenceEditable}
      isPrescriber={isPrescriber}
    />
  );
}
